import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { TypeDefaultImage } from "@/lib/image";
import { BO_LIST_ITEM_ID_ATTR } from "@/lib/dashboardListScrollRestore";
import { Attachment } from "@/lib/types";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface RelatedLinkGridCardShellProps {
  attachments: Attachment[];
  imageDomain: TypeDefaultImage;
  imageAlt: string;
  /** Route vers la fiche d’édition (ex. `/edit/artwork/:id`). */
  detailPath: string;
  onTextClick?: () => void;
  /** For scroll restore targeting on dashboard grids. */
  listItemId?: string;
  children: ReactNode;
}

/**
 * Carte grille liens : clic image → plein écran ; clic zone texte → fiche.
 */
export function RelatedLinkGridCardShell({
  attachments,
  imageDomain,
  imageAlt,
  detailPath,
  onTextClick,
  listItemId,
  children,
}: RelatedLinkGridCardShellProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const openDetail = () => {
    if (onTextClick) {
      onTextClick();
      return;
    }
    if (detailPath && detailPath !== "#") {
      navigate(detailPath);
    }
  };

  return (
    <div
      className="min-h-[389px] flex-col justify-start items-start inline-flex max-w-[300px]"
      {...(listItemId ? { [BO_LIST_ITEM_ID_ATTR]: listItemId } : {})}
    >
      <div
        className="h-[251px] w-full max-w-[300px] shrink-0 overflow-hidden rounded-t-sm"
        role="presentation"
      >
        {attachments.length > 0 ? (
          <ImageFullscreen
            media={{
              attachments,
              type: imageDomain,
              alt: imageAlt,
              width: 300,
              height: 251,
              className: "h-[251px] w-full max-w-[300px] object-contain",
            }}
          />
        ) : (
          <div className="flex h-[251px] w-full items-center justify-center bg-slate-100 text-xs font-medium uppercase tracking-wide text-slate-400">
            {t("noImage")}
          </div>
        )}
      </div>
      <div
        role="button"
        tabIndex={0}
        className="flex min-h-0 w-full flex-1 cursor-pointer flex-col rounded-b-sm px-0.5 py-1 text-left outline-none transition-colors hover:bg-slate-50/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={openDetail}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDetail();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
